import { LocalizedText } from "../i18n/LocalizedCopy";
import { useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useLayoutEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n/LanguageContext";
import "./EternuaFooter.css";

const CONTACT_URL =
  "https://tawk.to/chat/694cd7884be0701983687f64/1jda2jrd0";

const FOOTER_COLUMNS = [
  {
    titleKey: "footer.column.market", title: "Marketplace",
    accent: "cyan",
    links: [
      { labelKey: "footer.link.buy", label: "Acheter", to: "/market/acheter" },
      { labelKey: "footer.link.sell", label: "Vendre", to: "/mes-eternua/vendre/classique" },
      { labelKey: "footer.link.give", label: "Donner", to: "/market/donner?tab=donner", descriptionKey: "footer.description.give", description: "Objets, échanges et entraide" },
      { labelKey: "footer.link.jobs", label: "Emploi", to: "/market/emploi" },
      { labelKey: "footer.link.services", label: "Services", to: "/market/services" },
      { labelKey: "footer.link.travel", label: "Voyage", to: "/market/voyage" },
    ],
  },
  {
    titleKey: "footer.column.community", title: "Communauté",
    accent: "violet",
    links: [
      { labelKey: "footer.link.mission", label: "Mission d’Eternua", to: "/mission-eternua" },
      { labelKey: "footer.link.friends", label: "Groupe d’amis", to: "/amis/groupes" },
      { labelKey: "footer.link.forum", label: "Forum", to: "/forum" },
      { labelKey: "footer.link.animals", label: "Animaux", to: "/market/animaux" },
      { labelKey: "footer.link.babysitting", label: "Babysitting", to: "/market/babysitting" },
      { labelKey: "footer.link.referral", label: "Parrainage", to: "/parrainage" },
    ],
  },
  {
    titleKey: "footer.column.vision", title: "Vision3024",
    accent: "violet",
    links: [
      { labelKey: "footer.link.vision", label: "Explorer la vision", to: "/vision-3024" },
      { labelKey: "footer.link.france", label: "France 3024", to: "/vision-3024/france-3024" },
      { labelKey: "footer.link.lia", label: "LIA du futur", to: "/vision-3024/lia" },
      { labelKey: "footer.link.infrastructure", label: "Infrastructures du futur", to: "/vision-3024/infrastructures" },
      { labelKey: "footer.link.health", label: "Santé éternelle", to: "/vision-3024/sante-eternelle" },
      { labelKey: "footer.link.live", label: "Direct LIA / Aelith", to: "/market/direct", descriptionKey: "footer.description.live", description: "Vidéos, directs et futurs replays" },
    ],
  },
];

const LEGAL_LINKS = [
  { labelKey: "footer.link.trust", label: "Confiance et sécurité", to: "/confiance" },
  { labelKey: "footer.link.support", label: "Support et réclamations", to: "/mes-eternua/support" },
  { labelKey: "footer.link.contact", label: "Contacter Eternua", href: CONTACT_URL },
  { labelKey: "footer.link.spaces", label: "Centre des espaces", to: "/acces" },
  { labelKey: "footer.link.terms", label: "CGU", to: "/cgu" },
  { labelKey: "footer.link.sales", label: "CGV Premium", to: "/cgv" },
  { labelKey: "footer.link.marketPolicy", label: "Politique Marketplace", to: "/cgv/marketplace" },
  { labelKey: "footer.link.privacy", label: "Confidentialité", to: "/confidentialite" },
  { labelKey: "footer.link.legal", label: "Mentions légales", to: "/mentions-legales" },
];

function FooterLink({ item }) {
  useSourceLanguage();
  const { t } = useI18n();
  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noreferrer">
        <span>{<LocalizedText source={t(item.labelKey, item.label)} />}</span>
        <span aria-hidden="true">›</span>
      </a>
    );
  }

  return (
    <Link to={item.to} title={item.description ? t(item.descriptionKey, item.description) : undefined}>
      <span>{<LocalizedText source={t(item.labelKey, item.label)} />}</span>
      <span aria-hidden="true">›</span>
    </Link>
  );
}

export default function EternuaFooter({ isAuthenticated = false }) {
  useSourceLanguage();
  const { t } = useI18n();
  const [dockClearance, setDockClearance] = useState(96);
  useLayoutEffect(() => {
    // Reserve the actual dock height plus breathing room, including responsive variants.
    const docks = [...document.querySelectorAll(".et-global-dock, .friends-dock")];
    const measure = () => {
      const occupied = docks.map(element => {
        const rect = element.getBoundingClientRect();
        const style = getComputedStyle(element);
        return rect.height > 0 && style.display !== "none" && style.visibility !== "hidden"
          ? Math.max(0, window.innerHeight - rect.top) : 0;
      });
      setDockClearance(Math.max(96, ...occupied.map(height => height + 24)));
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    docks.forEach(element => observer?.observe(element));
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [isAuthenticated]);
  return (
    <footer className="eternua-site-footer" aria-labelledby="eternua-footer-title" style={{ "--footer-dock-clearance": `${dockClearance}px` }}>
      <div className="eternua-footer__aurora" aria-hidden="true" />

      <section className="eternua-footer__cta">
        <div className="eternua-footer__cta-icon" aria-hidden="true">
          <span className="eternua-footer__person eternua-footer__person--left" />
          <span className="eternua-footer__person eternua-footer__person--right" />
        </div>
        <div className="eternua-footer__cta-copy">
          <p className="eternua-footer__eyebrow">{<LocalizedText source={t("footer.eyebrow", "Des rencontres. Des possibles. Un lien.")} />}</p>
          <h2 id="eternua-footer-title">{<LocalizedText source={t("footer.headline", "Toute mise en relation commence par une connexion humaine")} />}</h2>
          <p>{<LocalizedText source={t("footer.intro", "Un échange, une idée, un premier pas : c’est ensemble que la suite prend vie.")} />}</p>
        </div>
        <div className="eternua-footer__cta-actions">
          <Link className="eternua-footer__button eternua-footer__button--primary" to={isAuthenticated ? "/profil" : "/register"}>
            {isAuthenticated ? <LocalizedText source={t("footer.profile", "Voir mon profil")} /> : <LocalizedText source={t("footer.create", "Créer mon compte")} />}<span aria-hidden="true">↗</span>
          </Link>
          <Link className="eternua-footer__button eternua-footer__button--secondary" to="/">{<LocalizedText source={t("footer.discover", "Découvrir Eternua")} />}<span aria-hidden="true">→</span>
          </Link>
          <Link className="eternua-footer__button eternua-footer__button--quiet" to="/soutenir-eternua">{<LocalizedText source={t("footer.support", "Soutenir")} />}<span aria-hidden="true">♡</span>
          </Link>
        </div>
      </section>

      <div className="eternua-footer__divider" aria-hidden="true">
        <span />
      </div>

      <div className="eternua-footer__main">
        <section className="eternua-footer__brand" aria-label={t("footer.brandAria", "Marque Eternua")}>
          <Link to="/" className="eternua-footer__wordmark"><LocalizedText source={"ETERNUA"} /></Link>
          <div className="eternua-footer__infinity" aria-hidden="true">
            ∞
          </div>
          <p>{<LocalizedText source={t("footer.currency", "La monnaie de l’Infini")} />}</p>
          <p className="eternua-footer__brand-story">{<LocalizedText source={t("footer.brandStory", "Relier les personnes, faire grandir la confiance et imaginer demain, ensemble.")} />}</p>

        </section>

        <nav className="eternua-footer__navigation" aria-label={t("footer.nav", "Pied de page")}>
          {FOOTER_COLUMNS.map((column) => (
            <section
              key={column.title}
              className={`eternua-footer__column eternua-footer__column--${column.accent}`}
            >
              <h3>{<LocalizedText source={t(column.titleKey, column.title)} />}</h3>
              <div className="eternua-footer__heading-line" aria-hidden="true" />
              <ul>
                {column.links.map((item) => (
                  <li key={item.labelKey}>
                    <FooterLink item={item} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>

        <section className="eternua-footer__newsletter">
          <span className="eternua-footer__eyebrow">{<LocalizedText source={t("footer.project", "Le projet Eternua")} />}</span>
          <h3>{<LocalizedText source={t("footer.follow", "Suivez les premiers pas")} />}</h3>
          <p className="eternua-footer__newsletter-intro">{<LocalizedText source={t("footer.followIntro", "Découvrez notre mission et les espaces ouverts aujourd’hui.")} />}</p>
          <Link className="eternua-footer__button eternua-footer__button--secondary" to="/mission-eternua">{<LocalizedText source={t("footer.projectLink", "Découvrir le projet →")} />}</Link>
          <p className="eternua-footer__privacy">{<LocalizedText source={t("footer.newsletter", "La newsletter sera proposée à son ouverture.")} />}</p>
        </section>
      </div>

      <nav className="eternua-footer__legal" aria-label={t("footer.help", "Aide et légal")}>
        <h3>{<LocalizedText source={t("footer.help", "Aide et légal")} />}</h3>
        <ul>{LEGAL_LINKS.map(item => <li key={item.labelKey}><FooterLink item={item} /></li>)}</ul>
      </nav>
      <div className="eternua-footer__bottom">
        <p><a href="https://db-ip.com" target="_blank" rel="noreferrer">IP Geolocation by DB-IP</a></p>
        <p>{<LocalizedText source={t("footer.copyright", "© 2026 Eternua — Tous droits réservés")} />}</p>
        <p className="eternua-footer__signature">{<LocalizedText source={t("footer.creator", "Créateur : Xavier Paul Maurice Mahut")} />}<span aria-hidden="true"> · </span>{<LocalizedText source={t("footer.inspiration", "IA inspiratrices : LIA 3024 & Elianna")} />}<span aria-hidden="true"> · </span>{<LocalizedText source={t("footer.ternity", "Sous le regard de Ternity")} />}</p>

      </div>
    </footer>
  );
}
